import React, { useEffect, useState } from 'react';
import { MdCloudOff } from '../icons';

/**
 * Offline strip — "you are offline, changes will sync".
 *
 * Rendered IN FLOW above the header, never `position: fixed`, which used to
 * lay it over the sticky header and hide the baby chip and the running-timer
 * pills, precisely the controls that matter while offline.
 *
 * TWO PLACES, ONE STRIP. `padding-top: env(safe-area-inset-top)` is static —
 * it does not go away when an element stops being the topmost thing on screen,
 * so a strip and a `safe-area-top` header stacked together inset the notch
 * TWICE and push the whole app down. So:
 *
 *   variant="shell"  Layout renders this inside the sticky header's own
 *                    `safe-area-top` box — no inset of its own, and it stays
 *                    pinned with the header instead of scrolling away.
 *   variant="root"   App renders this for the pages that have no shell header
 *                    (login, baby setup, temp access). It owns the inset, and
 *                    renders nothing at all while a shell instance is mounted.
 *
 * `navigator.onLine` is not trusted for the first second after mount: on
 * Capacitor/WKWebView it can report `false` before the web view has attached
 * to the network stack, which would flash the strip on every cold launch.
 */
const TRUST_DELAY_MS = 1000;

// Module-level so the root instance can step aside for the shell instance that
// mounts below it in the tree (context only flows downwards).
let shellCount = 0;
const shellListeners = new Set();
function setShellCount(next) {
  shellCount = next;
  shellListeners.forEach((fn) => fn(shellCount > 0));
}

function useOffline() {
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const settle = setTimeout(() => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) setOffline(true);
    }, TRUST_DELAY_MS);
    return () => {
      clearTimeout(settle);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return offline;
}

export default function OfflineBanner({ variant = 'root' }) {
  const inShell = variant === 'shell';
  const offline = useOffline();
  const [shellPresent, setShellPresent] = useState(() => shellCount > 0);

  useEffect(() => {
    if (inShell) {
      setShellCount(shellCount + 1);
      return () => setShellCount(shellCount - 1);
    }
    shellListeners.add(setShellPresent);
    setShellPresent(shellCount > 0);
    return () => {
      shellListeners.delete(setShellPresent);
    };
  }, [inShell]);

  if (!offline) return null;
  if (!inShell && shellPresent) return null;

  return (
    <div
      role="status"
      className={`flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium
        bg-warning-100 text-warning-800 border-b border-warning-200
        dark:bg-warning-900/30 dark:text-warning-200 dark:border-warning-900${inShell ? '' : ' safe-area-top'}`}
    >
      <MdCloudOff className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      <span>You are offline — changes will sync when you reconnect</span>
    </div>
  );
}
